/**
 * The Loans portal pages, placed in the six portal sections — the same shape as src/platforms/retail/retailPages.ts.
 *
 * `action` is the pcb-loans-api capability action id (GET v3/loans/admin/capabilities) that must be in the signed-in admin's
 * answer for the page to be listed: visibility follows the API's own answer. Hiding a page is a convenience only; the API still
 * checks every call. Add a line here (and a <Route> under "loans" in App.tsx, unless it is a writeups/ page) when a page lands.
 */

import type { SectionId } from '@/platforms/types';

export const LOANS_BASE = '/loans';

export interface LoansPage {
  /** Full path, under /loans. */
  path: string;
  label: string;
  section: SectionId;
  /** Heading the page is grouped under on its section page. */
  group: string;
  /** Capability action id that makes the page visible. */
  action: string;
}

const p = (path: string, label: string, section: SectionId, group: string, action: string): LoansPage => ({
  path: `${LOANS_BASE}/${path}`,
  label,
  section,
  group,
  action,
});

export const LOANS_PAGES: ReadonlyArray<LoansPage> = [
  // Find
  p('find/records', 'Find a record', 'find', 'Records', 'find.records'),

  // Monitoring
  p('monitoring/approvals', 'Approvals monitor', 'monitoring', 'Approvals', 'monitoring.approvals'),
  p('monitoring/jobs', 'Scheduled jobs', 'monitoring', 'Jobs', 'monitoring.jobs'),

  // Configuration
  p('configuration/encompass', 'Encompass milestones and webhook', 'configuration', 'Encompass', 'configuration.encompass.list'),
  p('configuration/settings', 'Loans settings', 'configuration', 'Settings', 'configuration.settings.list'),
  p('configuration/notices', 'Splash notices', 'configuration', 'Notices', 'configuration.notices.list'),

  // Access and roles
  p('access/people', 'People and personas', 'access', 'People', 'access.people.list'),

  // Tools
  p('tools/jx-probe', 'Core inquiry probe', 'tools', 'Diagnostics', 'tools.jx-probe'),
  p('tools/test-card', 'Test approval card', 'tools', 'Diagnostics', 'tools.test-card'),
];

/** The pages whose capability action is in the API's answer for this admin. */
export function loansPagesFor(actionIds: ReadonlyArray<string>): LoansPage[] {
  const have = new Set(actionIds);
  return LOANS_PAGES.filter((pg) => have.has(pg.action));
}
